import { toast as sonnerToast, type ExternalToast } from 'ngx-sonner';
import { firstValueFrom, isObservable, type Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
    normalizeToProblemDetails,
    toProblemToastCopy,
    type ProblemToastCopyOptions,
} from './http';

export type QpxTaskSource<T> =
    | PromiseLike<T>
    | Observable<T>
    | (() => PromiseLike<T> | Observable<T>);

export type QpxTaskOptions<T> = Omit<ExternalToast, 'description'> & {
    loading?: string;
    success?: string | ((data: T) => string);
    error?: ProblemToastCopyOptions;
    finally?: () => void | Promise<void>;
};

/**
 * Quipex toast adapter wrapping ngx-sonner's toast API.
 * - Preserves the same method signatures
 * - Applies sensible defaults per type (e.g., error duration)
 */
export const qpxToast = Object.assign(
    (
        ...rawArgs: Parameters<typeof sonnerToast>
    ): ReturnType<typeof sonnerToast> => {
        const [messageOrComponent, opts] = rawArgs;

        if (typeof messageOrComponent === 'string') {
            const message = messageOrComponent;
            return sonnerToast(message, opts);
        }

        const { componentProps, ...restOpts } = opts ?? {};
        const id = uuidv4();
        const component = messageOrComponent;

        const withIdArgs: ExternalToast = {
            ...restOpts,
            id,
            componentProps: {
                ...componentProps,
                toastId: id,
            },
        };
        return sonnerToast(component, withIdArgs);
    },
    {
        success: (
            ...args: Parameters<typeof sonnerToast.success>
        ): ReturnType<typeof sonnerToast.success> =>
            sonnerToast.success(...args),

        info: (
            ...args: Parameters<typeof sonnerToast.info>
        ): ReturnType<typeof sonnerToast.info> => sonnerToast.info(...args),

        warning: (
            ...args: Parameters<typeof sonnerToast.warning>
        ): ReturnType<typeof sonnerToast.warning> =>
            sonnerToast.warning(...args),

        error: (
            ...rawArgs: Parameters<typeof sonnerToast.error>
        ): ReturnType<typeof sonnerToast.error> => {
            const [message, opts] = rawArgs;
            const withDefaults: ExternalToast = {
                duration: 8_000,
                ...opts,
            };
            return sonnerToast.error(message, withDefaults);
        },

        loading: (
            ...args: Parameters<typeof sonnerToast.loading>
        ): ReturnType<typeof sonnerToast.loading> =>
            sonnerToast.loading(...args),

        /**
         * @deprecated Use qpxToast.task(...) so async errors can use Quipex ProblemDetails copy.
         */
        promise: (
            ...args: Parameters<typeof sonnerToast.promise>
        ): ReturnType<typeof sonnerToast.promise> =>
            sonnerToast.promise(...args),

        task: <T>(
            source: QpxTaskSource<T>,
            options: QpxTaskOptions<T>
        ): string | number | undefined => {
            const {
                loading,
                success,
                error,
                finally: onFinally,
                ...rest
            } = options;
            const id = loading ? qpxToast.loading(loading, rest) : undefined;
            const resolvedTask = resolveTask(source);
            let shouldDismiss = id !== undefined;

            void resolvedTask
                .then((response) => {
                    if (success !== undefined) {
                        const message =
                            typeof success === 'function'
                                ? success(response)
                                : success;
                        qpxToast.success(
                            message,
                            withOptionalToastId(rest, id)
                        );
                        shouldDismiss = false;
                    }
                })
                .catch((caughtError: unknown) => {
                    if (error !== undefined) {
                        const problemDetails =
                            normalizeToProblemDetails(caughtError);
                        const copy = toProblemToastCopy(problemDetails, error);
                        qpxToast.error(copy.message, {
                            ...withOptionalToastId(rest, id),
                            ...(copy.description
                                ? { description: copy.description }
                                : {}),
                        });
                        shouldDismiss = false;
                    }
                })
                .finally(() => {
                    if (shouldDismiss && id !== undefined) {
                        qpxToast.dismiss(id);
                    }

                    return onFinally?.();
                })
                .catch(() => undefined);

            return id;
        },

        custom: (
            ...rawArgs: Parameters<typeof sonnerToast.custom>
        ): ReturnType<typeof sonnerToast.custom> => {
            const [component, opts] = rawArgs;
            const { componentProps, ...restOpts } = opts ?? {};
            const id = uuidv4();

            const withIdArgs: ExternalToast = {
                ...restOpts,
                id,
                componentProps: {
                    ...componentProps,
                    toastId: id,
                },
            };

            return sonnerToast.custom(component, withIdArgs);
        },

        dismiss: (
            ...args: Parameters<typeof sonnerToast.dismiss>
        ): ReturnType<typeof sonnerToast.dismiss> =>
            sonnerToast.dismiss(...args),
    }
);

export type QpxToast = typeof qpxToast;

async function resolveTask<T>(source: QpxTaskSource<T>): Promise<T> {
    const resolvedSource = typeof source === 'function' ? source() : source;

    if (isObservable(resolvedSource)) {
        return await firstValueFrom(resolvedSource);
    }

    return await resolvedSource;
}

function withOptionalToastId(
    options: ExternalToast,
    id: string | number | undefined
): ExternalToast {
    return id === undefined ? options : { ...options, id };
}
